import { Link, useParams } from "react-router-dom";
import useSWR from "swr";
import { mutate } from "swr";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { SignedAvatarImage } from "@/components/signed-avatar-image";
import { BotProfileDialog } from "@/components/bots/bot-profile-dialog";
import { PageBreadcrumb } from "@/components/page-breadcrumb";
import { Section } from "@/components/section";
import { Chip } from "@/components/badge/chip";
import { fetcher, roleLabel } from "@/lib/swr";
import type { User } from "@/lib/swr";
import { DeleteUserButton } from "@/components/admin/user/delete-user-button";
import { EditRolesDialog } from "./EditRolesDialog";
import { ListEmpty } from "@/components/list/status-text";
import { EditVisibleAppsDialog } from "./EditVisibleAppsDialog";
import { TransferPrdsButton } from "./UserRoles";
import { ListLoading } from "@/components/list/status-text";
import { DisableBotButton } from "@/components/admin/user/disable-bot-button";

type VisibleApp = NonNullable<User["visibleApps"]>[number];

// Admin user detail: the row in the list stays slim (chip counts only);
// everything manageable about one user lives here — permissions, visible
// apps, PRD handover, deletion.
const AdminUserDetail = () => {
  const { id } = useParams();
  const { data: users, isLoading } = useSWR<User[]>(["/users/full"], fetcher);
  const user = users?.find((u) => String(u.id) === id);

  if (isLoading) return <ListLoading>Loading user…</ListLoading>;
  if (!user)
    return (
      <div className="text-sm text-muted-foreground">
        User not found.{" "}
        <Link to="/admin" className="text-primary hover:underline">
          Back to Admin
        </Link>
      </div>
    );

  const roles: string[] = user.roles || [];
  const apps: VisibleApp[] = (user.visibleApps || []).filter((app) => !app.archived);

  return (
    <div className="flex max-w-3xl flex-col gap-8">
      <PageBreadcrumb
        parent={{ label: "Admin", to: "/admin" }}
        current={user.name ?? String(user.id)}
      />

      {/* Header */}
      <div className="flex items-center gap-3">
        <Avatar className="h-12 w-12">
          <SignedAvatarImage src={user.avatarThumb} alt={user.name} />
          <AvatarFallback>{user.name?.[0]}</AvatarFallback>
        </Avatar>
        <div className="min-w-0">
          <h1 className="flex items-center gap-2 text-xl font-semibold leading-tight">
            {user.name}
            {user.kind === "bot" && <Chip className="px-1.5">bot</Chip>}
          </h1>
          <p className="truncate text-sm text-muted-foreground">
            {user.email || "—"}
            <span className="ml-3 font-mono text-xs">{String(user.openId ?? "")}</span>
          </p>
        </div>
        {/* Bots have no Feishu profile to sync from: name / avatar are edited
            here (people's come from Feishu on every login). */}
        {user.kind === "bot" && (
          <div className="ml-auto">
            <BotProfileDialog
              bot={user}
              endpoint={`/bots/${user.id}`}
              onSaved={() =>
                mutate(
                  (key) =>
                    Array.isArray(key) &&
                    (key[0] === "/users/full" || key[0] === "/users"),
                  undefined,
                  { revalidate: true }
                )
              }
            />
          </div>
        )}
      </div>

      <Section title="Roles" action={<EditRolesDialog user={user} />}>
        <div className="flex flex-wrap gap-1.5">
          {/* isAdmin is read-only here: admin is granted via DB only */}
          {user.isAdmin && (
            <Chip tone="red" className="px-2">
              admin
            </Chip>
          )}
          {roles.map((r) => (
            <Chip key={r} tone="green" className="px-2">
              {roleLabel(r)}
            </Chip>
          ))}
          {roles.length === 0 && !user.isAdmin && (
            <ListEmpty>No roles.</ListEmpty>
          )}
        </div>
      </Section>

      <Section title="Visible Apps" action={<EditVisibleAppsDialog user={user} />}>
        {apps.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No active apps.
          </p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {apps.map((app) => (
              <Chip key={app.id} tone="blue" className="px-2 font-normal">
                {app.name}
              </Chip>
            ))}
          </div>
        )}
      </Section>

      <Section title="Actions">
        <div className="flex items-center gap-3">
          {user.kind === "bot" ? (
            // Disabling a bot hands its PRDs to a picked user in the same
            // step — a bot never comes back to reclaim them.
            <DisableBotButton user={user} />
          ) : (
            <>
              <TransferPrdsButton user={user} />
              <DeleteUserButton user={user} />
            </>
          )}
        </div>
      </Section>
    </div>
  );
};

export default AdminUserDetail;
